import { FC, useState, useEffect } from 'react';
import {
    TextField,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    InputAdornment,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BadgeIcon from '@mui/icons-material/Badge';
import AppsIcon from '@mui/icons-material/Apps';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import UpdateIcon from '@mui/icons-material/Update';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { RoleModel, UpdateRoleRequest } from '../../../types/models/role.models';
import { SCUIButton } from '../../../atomic/atoms';

interface IProps {
    selectedRole?: RoleModel;
    onUpdateRole?: (roleData: UpdateRoleRequest) => void;
    onCloseUpdateRole?: () => void;
}

export const SCUIUpdateRoleDialogContent: FC<IProps> = ({ selectedRole, onUpdateRole, onCloseUpdateRole }) => {
    const { t, i18n } = useTranslation();
    const [roleInput, setRoleInput] = useState<UpdateRoleRequest>({
        roleId: 0,
        applicationId: 1,
        roleNameKey: '',
        isRequestable: false,
        description: '',
    });
    const [expandedPanels, setExpandedPanels] = useState<string[]>(['panel1', 'panel2', 'panel3']);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            setExpandedPanels([...expandedPanels, panel]);
        } else {
            setExpandedPanels(expandedPanels.filter((p) => p !== panel));
        }
    };

    useEffect(() => {
        if (selectedRole) {
            setRoleInput({
                roleId: selectedRole.roleId,
                applicationId: selectedRole.applicationId,
                roleNameKey: selectedRole.roleNameKey,
                isRequestable: selectedRole.isRequestable,
                description: selectedRole.description || '',
            });
        }
    }, [selectedRole]);

    const submitHandler = (event: React.FormEvent) => {
        event.preventDefault();

        // Get current user info for updatedBy field
        const currentOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');

        const roleData: UpdateRoleRequest = {
            ...roleInput,
            updatedBy: currentOperator?.userRef || undefined,
        };

        if (onUpdateRole) {
            onUpdateRole(roleData);
        }
    };

    return (
        <div>
            <form onSubmit={submitHandler}>
                <DialogContent
                    sx={{
                        pb: 2,
                        background: '#f8f9fa',
                        minHeight: '60vh',
                    }}
                >
                    {/* Genel Bilgiler Accordion */}
                    <Accordion
                        expanded={expandedPanels.includes('panel1')}
                        onChange={handleAccordionChange('panel1')}
                        sx={{
                            mb: 2,
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e0e0e0',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#5e72e4' }} />}
                            sx={{
                                backgroundColor: '#ffffff',
                                borderBottom: '1px solid #e0e0e0',
                                minHeight: '56px',
                                '&:hover': {
                                    backgroundColor: '#f7f8fc',
                                },
                                '& .MuiAccordionSummary-content': {
                                    margin: '12px 0',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <InfoIcon sx={{ color: '#5e72e4', fontSize: 24 }} />
                                <Box
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        color: '#32325d',
                                    }}
                                >
                                    {t('general.generalInformation')}
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    margin="dense"
                                    id="roleNameKey"
                                    label={t('authorizationManagement.roleName')}
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={roleInput.roleNameKey}
                                    onChange={(e) => setRoleInput({ ...roleInput, roleNameKey: e.target.value })}
                                    required
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{ color: '#5e72e4' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '&:hover fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                        },
                                    }}
                                />

                                <TextField
                                    margin="dense"
                                    id="applicationId"
                                    label={t('general.applicationId')}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={roleInput.applicationId}
                                    onChange={(e) =>
                                        setRoleInput({
                                            ...roleInput,
                                            applicationId: Number(e.target.value),
                                        })
                                    }
                                    required
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AppsIcon sx={{ color: '#5e72e4' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '&:hover fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                        },
                                    }}
                                />

                                <TextField
                                    margin="dense"
                                    id="description"
                                    label={t('general.description')}
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    helperText={`${(roleInput.description || '').length}/1000`}
                                    value={roleInput.description}
                                    onChange={(e) => setRoleInput({ ...roleInput, description: e.target.value })}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { maxLength: 1000 },
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                                    <DescriptionIcon sx={{ color: '#5e72e4' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '&:hover fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5e72e4',
                                            },
                                        },
                                    }}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={roleInput.isRequestable}
                                            onChange={(e) =>
                                                setRoleInput({
                                                    ...roleInput,
                                                    isRequestable: e.target.checked,
                                                })
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={t('roleApproval.isRequestable')}
                                    sx={{ mt: 2 }}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Oluşturma Bilgileri Accordion */}
                    <Accordion
                        expanded={expandedPanels.includes('panel2')}
                        onChange={handleAccordionChange('panel2')}
                        sx={{
                            mb: 2,
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e0e0e0',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#2dce89' }} />}
                            sx={{
                                backgroundColor: '#ffffff',
                                borderBottom: '1px solid #e0e0e0',
                                minHeight: '56px',
                                '&:hover': {
                                    backgroundColor: '#f7f8fc',
                                },
                                '& .MuiAccordionSummary-content': {
                                    margin: '12px 0',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PersonIcon sx={{ color: '#2dce89', fontSize: 24 }} />
                                <Box
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        color: '#32325d',
                                    }}
                                >
                                    {t('general.creationInformation')}
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    margin="dense"
                                    label={t('general.createdBy')}
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={selectedRole?.createdBy || '-'}
                                    disabled
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon sx={{ color: '#2dce89' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />

                                {selectedRole?.createdDate && (
                                    <TextField
                                        margin="dense"
                                        label={t('general.createdDate')}
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={new Date(selectedRole.createdDate).toLocaleString(i18n.language, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                        disabled
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarTodayIcon sx={{ color: '#2dce89' }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Güncelleme Bilgileri Accordion */}
                    <Accordion
                        expanded={expandedPanels.includes('panel3')}
                        onChange={handleAccordionChange('panel3')}
                        sx={{
                            mb: 2,
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e0e0e0',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#11cdef' }} />}
                            sx={{
                                backgroundColor: '#ffffff',
                                borderBottom: '1px solid #e0e0e0',
                                minHeight: '56px',
                                '&:hover': {
                                    backgroundColor: '#f7f8fc',
                                },
                                '& .MuiAccordionSummary-content': {
                                    margin: '12px 0',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <UpdateIcon sx={{ color: '#11cdef', fontSize: 24 }} />
                                <Box
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        color: '#32325d',
                                    }}
                                >
                                    {t('general.updateInformation')}
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    margin="dense"
                                    label={t('general.lastUpdatedBy')}
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={selectedRole?.lastUpdatedBy || '-'}
                                    disabled
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonOutlineIcon sx={{ color: '#11cdef' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />

                                {selectedRole?.updatedDate && (
                                    <TextField
                                        margin="dense"
                                        label={t('general.updatedDate')}
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={new Date(selectedRole.updatedDate).toLocaleString(i18n.language, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                        disabled
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarTodayIcon sx={{ color: '#11cdef' }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        borderTop: '1px solid #e0e0e0',
                        gap: 2,
                        justifyContent: 'center',
                    }}
                >
                    <SCUIButton
                        onClick={onCloseUpdateRole}
                        type="secondary"
                        buttonType="button"
                        sizeType="fixed"
                        size="m"
                        text={t('general.cancel')}
                        showStartIcon
                        startIcon={<CloseIcon />}
                    />
                    <SCUIButton
                        type="primary"
                        buttonType="submit"
                        sizeType="fixed"
                        size="m"
                        text={t('general.update')}
                        showStartIcon
                        startIcon={<SaveIcon sx={{ color: '#ffffff' }} />}
                    />
                </DialogActions>
            </form>
        </div>
    );
};
